import { Localizable } from '@components/localizable/Localizable'
import TipTapEditor from '@components/tiptap/TipTapEditor'
import { useLocalizable } from '@contexts/LocalizableContext'
import { LegalDocumentVersion as LegalDocumentVersionModel } from '@models/LegalDocument'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  selectLegalDocumentBySlug,
  updateLegalDocumentActiveVersion,
} from '@store/LegalDocumentReducer'
import { selectSelectedLegalDocumentVersion } from '@store/LegalDocumentVersionReducer'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  message,
  Spin,
  Tag,
  Typography,
} from 'antd'
import { FC, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { DEFAULT_ERROR, DEFAULT_SUCCESS } from '../../App'

const { Text } = Typography

type Props = SBModelComponent<LegalDocumentVersionModel> & {
  onRefresh: () => void
}

const LegalDocumentVersionRead: FC<Props> = ({ onRefresh }) => {
  const { legalDocumentSlug, version } = useParams()
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const localizable = useLocalizable()

  const legalDocument = useAppSelector(
    selectLegalDocumentBySlug(legalDocumentSlug ?? '')
  )
  const item = useAppSelector(selectSelectedLegalDocumentVersion())

  const { isLoading } = useAppSelector((state) => state.legalDocumentVersion)

  useEffect(() => {
    if (!item || !localizable) return

    form.setFieldsValue({
      ...item,
      title: item[localizable.langCode].title,
      body: item[localizable.langCode].body,
    })
  }, [item, localizable, form])

  if (isLoading) {
    return (
      <Card>
        <Spin />
      </Card>
    )
  }

  if (!legalDocumentSlug || !legalDocument) return

  if (!item) {
    return (
      <Card>
        <Text>
          The version <b>{version}</b> doesn't exist.
          <br />
          Please select a valid version on the left.
        </Text>
      </Card>
    )
  }

  const handleActive = async () => {
    try {
      await dispatch(updateLegalDocumentActiveVersion(legalDocument, item.id))

      message.success(DEFAULT_SUCCESS)

      onRefresh()
    } catch {
      message.error(DEFAULT_ERROR)
    }
  }

  return (
    <Card
      title={`V${item.version}`}
      extra={
        item.isActive ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Button onClick={handleActive}>Activate this version</Button>
        )
      }
    >
      <Flex justify="end">
        <Localizable.LangSelector />
      </Flex>

      <Form form={form} layout="vertical" disabled>
        <Form.Item name={'title'} label="Title">
          <Input max={60} />
        </Form.Item>

        <Form.Item name={'body'} label="Content">
          <TipTapEditor mode="full" disabled />
        </Form.Item>
      </Form>
    </Card>
  )
}

type LegalDocumentVersionReadType = { Read: typeof LegalDocumentVersionRead }

export { LegalDocumentVersionRead, type LegalDocumentVersionReadType }
